import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { BodyParking } from '@shared/helpers/BodyParking';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface DeletePopupResult {
  toggle: boolean;
  obj: unknown;
  deleteFolder: boolean;
}

// Unique id seed: this component is mounted many times at once, so every id it renders has to be
// per-instance. That covers the checkbox <input id>/<label for> pair (label clicks would otherwise
// toggle the wrong, hidden instance's checkbox) AND the modal element itself — a hardcoded
// id="deletePopup" produced ~13 duplicate ids in one document, so getElementById / #deletePopup
// resolved to a hidden unbound instance whose heading always read a nameless "Delete ?".
// Styling hangs off the .deletePopup CLASS instead (see style.css, nested-confirmation overrides).
let nextDeletePopupId = 0;

@Component({
  selector: 'vf-delete-popup',
  template: ` <div bsModal #customModal="bs-modal" class="modal fade center-modal trovve-sm-modal z-index-1110 deletePopup" [attr.id]="modalId" tabindex="-1" [config]="{backdrop: 'static',  keyboard: false}">
  <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" style="max-width: 510px;">
      <div class="modal-content box-shadow-popup border-all bg-pure-white" style="padding: 24px !important;">
          <div class="modal-body py-0 px-0 bg-pure-white">
              <h1 class="f-size-16 tk-montserrat text-vf-dark font-weight-semibold mb-2">{{ actionName ? actionName : 'Delete' }}<span class="text-vf-dark" *ngIf="entityDisplayTitle"> "{{ entityDisplayTitle }}"</span>?</h1>
              <p class="f-size-14 text-medium font-weight-normal mb-0 line-height-20" *ngIf="lede">{{ lede }}</p>
              <p class="f-size-14 text-medium font-weight-normal mb-0 mt-2 line-height-20" *ngIf="note">{{ note }}</p>
              <ul class="f-size-14 text-medium font-weight-normal mb-0 mt-2 line-height-20 pl-3" *ngIf="lines.length">
                  <li *ngFor="let line of lines; trackBy: trackByLine">{{ line }}</li>
              </ul>
              <div class="d-flex align-items-center pt-3" *ngIf="showFolderOption">
                  <div class="custom-control custom-checkbox custom-checkbox-blue">
                      <input type="checkbox" class="custom-control-input" [(ngModel)]="folderChecked" [attr.id]="folderOptionId">
                      <label class="custom-control-label pt-1" [attr.for]="folderOptionId">{{ folderOptionLabel }}</label>
                  </div>
              </div>
          </div>
          <div class="modal-footer d-flex px-0 pb-0 pt-2 border-0 justify-content-end bg-pure-white">
            <button style="min-width: 80px;" type="button"
              class="btn f-size-14 text-gray-700 rounded-8 py-10 hover:bg-gray-100 transition-colors border-0 bg-transparent mr-2 m-0 line-height-16"
              (click)="clickOk(false)">Cancel
            </button>
            <button *ngIf="!hideConfirm" style="min-width: 80px;" type="button" (click)="clickOk(true)"
              class="btn bg-red-600 text-white hover:bg-red-700 rounded-8 py-10 m-0 line-height-16">{{ confirmLabel || actionName || 'Delete' }}
            </button>
          </div>
      </div>
  </div>
</div>`
})
export class DeletePopupComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() entity: string;
  @Input() entityObj: any;
  @Input() isOpenFromSlider: boolean;
  @Input() actionName: string;
  /**
   * An extra sentence under "This action cannot be undone.", for a removal whose reach the caller
   * cannot show on screen. Project templates use it to say that a role is shared across every
   * template (spec §3.7) and how much of the open template it is on. Empty everywhere else, so no
   * existing consumer changes.
   */
  @Input() note = '';
  /**
   * The first line under the heading. Every consumer but one deletes for good, so it defaults to the
   * sentence this dialog has always shown. The template grid's row X passes its own: that delete is
   * staged and Discard changes brings the row back, so "cannot be undone" would be untrue there.
   */
  @Input() lede = 'This action cannot be undone.';
  /** The red button's label when it should differ from the heading's verb ("Delete stage"). Empty everywhere else. */
  @Input() confirmLabel = '';
  /**
   * True when the delete is not possible and the dialog only explains why (a client stage people are in, Lead Pipeline
   * D12): Cancel is the one button. Off for every other consumer.
   */
  @Input() hideConfirm = false;
  /** When true, renders an opt-in "delete folder" checkbox (checked by default). Off for all other consumers. */
  @Input() showFolderOption = false;
  @Input() folderOptionLabel = 'Delete client folder as well';
  /** The checkbox's state on every open. Lead Pipeline LP6 (D7): people pass false while the Lead Pipeline is on. */
  @Input() folderDefault = true;
  /** Lead Pipeline LP6 (D13, §56): an impact preview's lines under the lede (a bulk Set stage / type). Empty everywhere else. */
  @Input() lines: readonly string[] = [];
  folderChecked = true;
  /** One sequence number per instance, shared by every id this component renders. Declared before the
   *  ids below so its initializer runs first. */
  private readonly instanceId = nextDeletePopupId++;
  readonly modalId = `deletePopup_${this.instanceId}`;
  readonly folderOptionId = `vfDeletePopupFolder_${this.instanceId}`;
  @ViewChild('customModal') customModal: ModalDirective;
  @ViewChild('customModal', { read: ElementRef }) private customModalEl?: ElementRef<HTMLElement>;
  @Output() okClick = new EventEmitter<DeletePopupResult>();
  /** The dialog always opens in the middle of the SCREEN, wherever it is declared (user, 2026-09-25): a
   *  host inside a slide-in panel would otherwise centre it on the panel. See BodyParking. */
  private readonly parking: BodyParking;
  private readonly destroy$ = new Subject<void>();

  constructor(host: ElementRef<HTMLElement>, renderer: Renderer2, @Inject(DOCUMENT) doc: Document) {
    this.parking = new BodyParking(renderer, doc, () => host.nativeElement, () => this.customModalEl?.nativeElement);
  }

  ngAfterViewInit(): void {
    this.customModal.onHidden.pipe(takeUntil(this.destroy$)).subscribe(() => this.parking.unpark());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.parking.unpark();
  }

  /** Fallback chain across the name field each consumer's row happens to carry. Consumers whose rows
   *  use none of the common names rendered a nameless "Delete ?" — the dialog never said what was
   *  about to be deleted. `templateName` covers the project-template rows from
   *  GetProjectTemplateLists; `roleName` the project-role rows; `sectionName` the project's own
   *  Manage sections rows (its delete confirm read "Delete ?" - a template section keeps its name on
   *  `.text` instead); `stage` covers client stages, whose name really is on `.stage` (see
   *  manage-client-stages, `dto.stage = stage.stage`). The last two are kept at the end so they can
   *  never outrank a row carrying a proper name field. `taskName` covers a project template's task
   *  rows, whose Remove confirm read "Delete ?" too; it sits after the other name fields so a row that
   *  carries its parent task's name beside its own (a step, say) keeps being called by its own. */
  get entityDisplayTitle(): string {
    if (!this.entityObj) return '';
    return this.entityObj.title || this.entityObj.projectName || this.entityObj.templateName
      || this.entityObj.name || this.entityObj.displayName
      || this.entityObj.roleName || this.entityObj.sectionName || this.entityObj.taskName
      || this.entityObj.stage || this.entityObj.text || '';
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityObj) {
      this.doSomething(changes.entityObj.currentValue);
    }
  }

  doSomething(condition) {
    if (condition) {
      this.folderChecked = this.folderDefault;
      this.parking.park();
      this.customModal.show();
      // $('body').addClass('deletePopup');
      if ($('body > *').hasClass('modal-backdrop')) {
        $('body').removeClass('task-modal-body');
        $('.modal-backdrop:not(:first)').removeClass('modal-backdrop');
        // } else {
        //   $('body').removeClass('task-modal-body');
        $('body').addClass('center-modal-shown');
      }
    }
  }

  trackByLine(index: number, line: string): string {
    return index + ':' + line;
  }

  clickOk(ev: boolean) {
    if ($('body').hasClass('center-modal-shown')) {
      if (!$('.right-side-bar-projects').hasClass('collapsed') ||
          $('#People').hasClass('show') ||
          !$('.right-side-bar-contacts').hasClass('collapsed') ||
          !$('.right-side-bar-files').hasClass('collapsed') ||
          $('.task-slider').hasClass('show') ||
          $('.task-modal-left').hasClass('show') ||
          $('.task-modal-right').hasClass('show')) {
            $('body').addClass('task-modal-body');
        }
      setTimeout(() => {
        $('body').removeClass('center-modal-shown');
      }, 1000);
    }
    // $('body').addClass('task-modal-body');
    this.customModal.hide();
    // setTimeout(() => {
    //   $('body').removeClass('deletePopup');
    // }, 500);
    this.okClick.next({ toggle: ev, obj: this.entityObj, deleteFolder: this.folderChecked });

  }
}
